import { Head, Link } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { FileText, Globe, Type } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { EmptyState } from "@/components/blocks/EmptyState"
import { TableCard } from "@/components/blocks/TableCard"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { SOURCE_KIND_LABELS } from "@/components/generations/format"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { SourceKind, SourceSummary } from "@/types/generations"

const SOURCE_ICONS: Record<SourceKind, typeof FileText> = { text: Type, url: Globe, file: FileText }

export default function SourcesIndex({ sources }: { sources: SourceSummary[] }) {
  const intl = useIntl()
  const { date, wordCount } = useFormat()
  const description = intl.formatMessage({ id: "sources.meta" })

  return (
    <>
      <Head title={intl.formatMessage({ id: "sources.title" })}>
        <meta name="description" content={description} />
        <meta property="og:title" content={intl.formatMessage({ id: "sources.title" })} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "sources.title" })}
          description={intl.formatMessage({ id: "sources.subtitle" })}
          actions={
            <>
              <Button asChild variant="secondary">
                <Link href="/generations/new?kind=deck">{intl.formatMessage({ id: "sources.generate_deck" })}</Link>
              </Button>
              <Button asChild>
                <Link href="/generations/new?kind=quiz">{intl.formatMessage({ id: "sources.generate_quiz" })}</Link>
              </Button>
            </>
          }
        />

        <div className="mt-8">
          {sources.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={intl.formatMessage({ id: "sources.empty_title" })}
              description={intl.formatMessage({ id: "sources.empty_body" })}
              action={
                <Button asChild>
                  <Link href="/generations/new?kind=deck">{intl.formatMessage({ id: "sources.generate_deck" })}</Link>
                </Button>
              }
            />
          ) : (
            <TableCard>
              <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
                <TableHead>
                  <TableRow>
                    <TableHeader>{intl.formatMessage({ id: "sources.col_source" })}</TableHeader>
                    <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "sources.col_type" })}</TableHeader>
                    <TableHeader className="hidden md:table-cell">{intl.formatMessage({ id: "sources.col_length" })}</TableHeader>
                    <TableHeader className="text-right">{intl.formatMessage({ id: "sources.col_added" })}</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map((source) => {
                    const Icon = SOURCE_ICONS[source.kind]
                    return (
                      <TableRow key={source.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Icon className="size-4 shrink-0 text-ink-muted" />
                            <Link
                              href={`/sources/${source.id}`}
                              className="font-medium text-ink-display no-underline after:absolute after:inset-0 hover:text-ink-display"
                            >
                              {source.title}
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell className="hidden text-ink-muted sm:table-cell">{intl.formatMessage(SOURCE_KIND_LABELS[source.kind])}</TableCell>
                        <TableCell className="hidden text-ink-muted md:table-cell">{wordCount(source.word_count)}</TableCell>
                        <TableCell className="text-right text-ink-muted">{date(source.created_on, { month: "short", day: "numeric", year: "numeric" })}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableCard>
          )}
        </div>
      </AppShell>
    </>
  )
}
